/*
 * Client/API compatibility check (SHER-323).
 *
 * On boot, fetch GET /version from the API. If the running client's
 * __APP_VERSION__ is below the API-advertised `minClientVersion`, show a
 * NON-BLOCKING "please refresh" banner and ask the PWA service worker to
 * update + reload so the user picks up the newer bundle.
 *
 * Everything here is best-effort and fully guarded: a missing endpoint, a
 * network failure, or no service worker must never block or crash the app.
 *
 * The banner is injected as a plain DOM node (no React mount) so it works
 * even before/independently of the React tree and can't be torn down by a
 * re-render. It's intentionally not a reusable UI component.
 */

import { semverLt } from "./telemetry";

const BANNER_ID = "sherwood-update-banner";

function safeApiBaseUrl() {
    try {
        if (typeof import.meta !== "undefined" && import.meta && import.meta.env) {
            const v = import.meta.env.VITE_APP_API_URL;
            if (typeof v === "string" && v) return v.replace(/\/$/, "");
        }
    } catch (_) { /* ignore */ }
    return "";
}

function currentAppVersion() {
    try { return typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0"; } catch (_) { return "0.0.0"; }
}

/** Ask the active service worker to update and reload once it activates. */
function triggerServiceWorkerUpdate() {
    try {
        if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
        navigator.serviceWorker.getRegistration()
            .then((reg) => {
                if (!reg) return;
                reg.update().catch(() => { /* ignore */ });
            })
            .catch(() => { /* ignore */ });
    } catch (_) { /* ignore */ }
}

/** Render the non-blocking refresh banner. Idempotent. */
function showUpdateBanner() {
    try {
        if (typeof document === "undefined") return;
        if (document.getElementById(BANNER_ID)) return;
        const banner = document.createElement("div");
        banner.id = BANNER_ID;
        banner.setAttribute("role", "status");
        banner.style.cssText = [
            "position:fixed", "left:0", "right:0", "bottom:0", "z-index:2147483647",
            "background:#1976d2", "color:#fff", "padding:10px 16px",
            "font-family:system-ui,Arial,sans-serif", "font-size:14px",
            "display:flex", "align-items:center", "justify-content:center",
            "gap:12px", "box-shadow:0 -2px 8px rgba(0,0,0,0.2)",
        ].join(";");

        const msg = document.createElement("span");
        msg.textContent = "A new version of the app is available. Please refresh to update.";

        const btn = document.createElement("button");
        btn.type = "button";
        btn.textContent = "Refresh";
        btn.style.cssText = [
            "background:#fff", "color:#1976d2", "border:none", "border-radius:4px",
            "padding:6px 14px", "font-size:14px", "cursor:pointer", "font-weight:600",
        ].join(";");
        btn.addEventListener("click", () => {
            try { triggerServiceWorkerUpdate(); } catch (_) { /* ignore */ }
            try { window.location.reload(); } catch (_) { /* ignore */ }
        });

        banner.appendChild(msg);
        banner.appendChild(btn);
        document.body.appendChild(banner);
    } catch (_) { /* never break app */ }
}

/**
 * Pure decision: given the running version and the /version response, decide
 * whether the client is too old. Exported for tests.
 */
function isClientOutdated(appVersion, versionResponse) {
    try {
        const min = versionResponse && versionResponse.minClientVersion;
        if (!min || typeof min !== "string") return false;
        return semverLt(appVersion, min);
    } catch (_) {
        return false;
    }
}

/** Boot-time entry point. Best-effort; never throws. */
async function runCompatibilityCheck() {
    try {
        if (typeof fetch !== "function") return;
        const base = safeApiBaseUrl();
        const resp = await fetch(`${base}/version`, { method: "GET" });
        if (!resp || !resp.ok) return;
        const data = await resp.json();
        if (isClientOutdated(currentAppVersion(), data)) {
            showUpdateBanner();
            triggerServiceWorkerUpdate();
        }
    } catch (_) { /* never break app */ }
}

export {
    runCompatibilityCheck,
    isClientOutdated,
    showUpdateBanner,
    triggerServiceWorkerUpdate,
};
